import { Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from "react";
import { Button, Tooltip } from "@fluentui/react-components";
import { Mic28Filled } from "@fluentui/react-icons";
import { Pause, Play, Square } from "lucide-react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useMsal } from "@azure/msal-react";

import styles from "./QuestionInput.module.css";
import { getToken, useMsalAuth } from "../../authConfig";
import { speechToTextApi } from "../../api";

interface Props {
    updateQuestion: Dispatch<SetStateAction<string>>;
    visualizerContainer?: HTMLElement | null;
    autoStartToken?: string | number | null;
    onAutoStartHandled?: () => void;
}

const WAVEFORM_BARS = 32;
const defaultWaveform = Array.from({ length: WAVEFORM_BARS }, () => 0.18);

const MIME_CANDIDATES = ["audio/webm;codecs=opus", "audio/ogg;codecs=opus", "audio/webm", "audio/mp4"];

const hasRecordingSupport = () =>
    typeof window !== "undefined" &&
    typeof navigator !== "undefined" &&
    Boolean(navigator.mediaDevices?.getUserMedia) &&
    "MediaRecorder" in window;

const getPreferredMimeType = () => {
    if (typeof window === "undefined" || !(window as any).MediaRecorder) {
        return "";
    }
    for (const type of MIME_CANDIDATES) {
        if ((window as any).MediaRecorder.isTypeSupported?.(type)) {
            return type;
        }
    }
    return "";
};

const extensionFromMime = (mime: string) => {
    if (mime.includes("ogg")) return "ogg";
    if (mime.includes("mp4")) return "mp4";
    if (mime.includes("wav")) return "wav";
    return "webm";
};

export const SpeechInput = ({ updateQuestion, visualizerContainer, autoStartToken, onAutoStartHandled }: Props) => {
    const { t, i18n } = useTranslation();
    const msalInstance = useMsalAuth ? useMsal().instance : undefined;

    const [isSupported, setIsSupported] = useState(false);
    const [isRecording, setIsRecording] = useState(false);
    const [isPaused, setIsPaused] = useState(false);
    const [isTranscribing, setIsTranscribing] = useState(false);
    const [waveformValues, setWaveformValues] = useState<number[]>(defaultWaveform);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [detectedLanguage, setDetectedLanguage] = useState<string | null>(null);

    const audioContextRef = useRef<AudioContext | null>(null);
    const analyserRef = useRef<AnalyserNode | null>(null);
    const mediaStreamRef = useRef<MediaStream | null>(null);
    const animationFrameRef = useRef<number>();
    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const audioChunksRef = useRef<Blob[]>([]);
    const transcriptionAbortRef = useRef<AbortController | null>(null);
    const recordingStartTimeRef = useRef<number>(0);
    const handledAutoStartTokenRef = useRef<Props["autoStartToken"]>(null);

    useEffect(() => {
        setIsSupported(hasRecordingSupport());
    }, []);

    useEffect(() => {
        return () => {
            if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
                mediaRecorderRef.current.stop();
            }
            transcriptionAbortRef.current?.abort();
            stopVisualizer();
            stopMediaStream();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const stopMediaStream = () => {
        if (mediaStreamRef.current) {
            mediaStreamRef.current.getTracks().forEach(track => track.stop());
            mediaStreamRef.current = null;
        }
    };

    const startVisualizer = (stream: MediaStream) => {
        const AudioContextCtor = (window.AudioContext || (window as any).webkitAudioContext) as typeof AudioContext | undefined;
        if (!AudioContextCtor) {
            throw new Error("audio-context-not-available");
        }
        // Use 44100 Hz sample rate like meetings do
        const audioContext = new AudioContextCtor({ sampleRate: 44100 });
        console.log("[SpeechInput] AudioContext created with sample rate:", audioContext.sampleRate);

        const analyser = audioContext.createAnalyser();
        // Smaller FFT and more aggressive scaling makes the visualizer feel "livelier"
        analyser.fftSize = 256;
        analyser.minDecibels = -100;
        analyser.maxDecibels = -10;
        analyser.smoothingTimeConstant = 0.4;
        const source = audioContext.createMediaStreamSource(stream);

        // Simple direct connection for visualization only (no audio feedback)
        source.connect(analyser);

        audioContextRef.current = audioContext;
        analyserRef.current = analyser;

        const bufferLength = analyser.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);

        const updateWaveform = () => {
            if (!analyserRef.current) {
                return;
            }
            analyserRef.current.getByteTimeDomainData(dataArray);
            const step = Math.max(1, Math.floor(bufferLength / WAVEFORM_BARS));
            const values = new Array(WAVEFORM_BARS).fill(0).map((_, index) => {
                const start = index * step;
                const end = Math.min(bufferLength, start + step);
                let total = 0;

                for (let sampleIndex = start; sampleIndex < end; sampleIndex += 1) {
                    const sample = dataArray[sampleIndex] ?? 128;
                    total += Math.abs(sample - 128) / 128;
                }

                const average = total / Math.max(1, end - start);
                return Math.min(1, Math.pow(average * 6, 0.72));
            });
            setWaveformValues(values);
            animationFrameRef.current = requestAnimationFrame(updateWaveform);
        };

        updateWaveform();
    };

    const stopVisualizer = () => {
        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
            animationFrameRef.current = undefined;
        }

        analyserRef.current = null;
        if (audioContextRef.current) {
            audioContextRef.current.close();
            audioContextRef.current = null;
        }
        setWaveformValues(defaultWaveform);
    };

    const createMediaRecorder = (stream: MediaStream) => {
        const mimeType = getPreferredMimeType();
        const options = mimeType ? { mimeType } : undefined;
        const recorder = new MediaRecorder(stream, options);
        recorder.addEventListener("dataavailable", event => {
            if (event.data?.size) {
                audioChunksRef.current.push(event.data);
                console.log(`[SpeechInput] Audio chunk received: ${event.data.size} bytes, total chunks: ${audioChunksRef.current.length}`);
            }
        });
        recorder.addEventListener("error", event => {
            console.error("[SpeechInput] MediaRecorder error:", event);
            setErrorMessage(t("voiceInput.errorGeneric", "We couldn't continue recording. Please try again."));
        });
        return recorder;
    };

    const beginRecording = async () => {
        console.log("[SpeechInput] Starting recording...");
        setErrorMessage(null);
        setDetectedLanguage(null);

        if (!hasRecordingSupport()) {
            setIsSupported(false);
            setErrorMessage(t("voiceInput.unsupported", "Voice input isn't supported in this browser."));
            return;
        }

        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            console.log("[SpeechInput] Got media stream, audio tracks:", stream.getAudioTracks().length);
            mediaStreamRef.current = stream;
            startVisualizer(stream);

            audioChunksRef.current = [];
            const recorder = createMediaRecorder(stream);
            const mimeType = recorder.mimeType || getPreferredMimeType();
            console.log("[SpeechInput] MediaRecorder created with MIME type:", mimeType);

            mediaRecorderRef.current = recorder;
            // Use 1000ms intervals like meeting recordings (was 250ms)
            recorder.start(1000);
            recordingStartTimeRef.current = Date.now();
            console.log("[SpeechInput] Recording started at:", new Date(recordingStartTimeRef.current).toISOString());

            setIsRecording(true);
            setIsPaused(false);
        } catch (error) {
            console.error("[SpeechInput] Unable to access microphone:", error);
            setErrorMessage(t("voiceInput.permissionError", "Microphone permissions are blocked. Please allow access and retry."));
            stopVisualizer();
            stopMediaStream();
        }
    };

    useEffect(() => {
        if (autoStartToken === undefined || autoStartToken === null) {
            return;
        }

        if (handledAutoStartTokenRef.current === autoStartToken || isRecording || isTranscribing) {
            return;
        }

        handledAutoStartTokenRef.current = autoStartToken;
        onAutoStartHandled?.();
        void beginRecording();
        // beginRecording is intentionally kept inline so it uses the current recorder state.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoStartToken, isRecording, isTranscribing, onAutoStartHandled]);

    const pauseRecording = () => {
        if (!mediaRecorderRef.current || mediaRecorderRef.current.state !== "recording") {
            return;
        }
        mediaRecorderRef.current.pause();
        setIsPaused(true);
    };

    const resumeRecording = () => {
        if (!mediaRecorderRef.current || mediaRecorderRef.current.state !== "paused") {
            return;
        }
        mediaRecorderRef.current.resume();
        setIsPaused(false);
    };

    const collectRecording = () =>
        new Promise<Blob | null>(resolve => {
            const recorder = mediaRecorderRef.current;
            if (!recorder) {
                resolve(null);
                return;
            }

            recorder.addEventListener(
                "stop",
                () => {
                    const mimeType = recorder.mimeType || getPreferredMimeType() || "audio/webm";
                    const blob = audioChunksRef.current.length ? new Blob(audioChunksRef.current, { type: mimeType }) : null;
                    audioChunksRef.current = [];
                    mediaRecorderRef.current = null;
                    stopVisualizer();
                    stopMediaStream();
                    resolve(blob);
                },
                { once: true }
            );

            if (recorder.state !== "inactive") {
                recorder.stop();
            } else {
                resolve(null);
            }
        });

    const insertTranscript = (transcript: string) => {
        updateQuestion(current => {
            if (!current) {
                return transcript;
            }
            return current.endsWith(" ") ? `${current}${transcript}` : `${current} ${transcript}`;
        });
    };

    const transcribeAudio = async (audioBlob: Blob) => {
        setIsTranscribing(true);
        setErrorMessage(null);
        const extension = extensionFromMime(audioBlob.type);
        const fileName = `voice-note-${Date.now()}.${extension}`;
        console.log(`[SpeechInput] Preparing to transcribe: ${fileName}, size: ${audioBlob.size} bytes, type: ${audioBlob.type}`);

        transcriptionAbortRef.current?.abort();
        const controller = new AbortController();
        transcriptionAbortRef.current = controller;

        // Set up a 30-second timeout
        const timeoutId = setTimeout(() => {
            console.warn("[SpeechInput] Transcription timeout - aborting request");
            controller.abort();
        }, 30000);

        try {
            let token: string | undefined;
            if (msalInstance) {
                try {
                    token = await getToken(msalInstance);
                } catch (tokenError) {
                    console.error("Failed to fetch token for speech-to-text", tokenError);
                }
            }

            const result = await speechToTextApi(audioBlob, fileName, token, controller.signal);

            // Clear the timeout since we got a response
            clearTimeout(timeoutId);
            console.log("[SpeechInput] Transcription response:", {
                status: result.status,
                hasText: !!result.text,
                textLength: result.text?.length || 0,
                detected_language: result.detected_language,
                message: result.message
            });
            setDetectedLanguage(result.detected_language ?? null);

            if (result.status === "not_configured") {
                setErrorMessage(
                    t(
                        "voiceInput.notConfigured",
                        "Speech transcription is not configured for this environment. Please contact your administrator."
                    )
                );
                return;
            }

            if (result.status === "file_too_large") {
                setErrorMessage(t("voiceInput.fileTooLarge", "Recording too long. Please try a shorter clip."));
                return;
            }

            if (result.status === "conversion_failed") {
                setErrorMessage(
                    t("voiceInput.conversionFailed", "We couldn't process that recording. Please try again with a new clip.")
                );
                return;
            }

            const transcript = (result.text as string | undefined)?.trim();

            if (transcript) {
                console.log(`[SpeechInput] Transcription successful: "${transcript.substring(0, 50)}..."`);
                insertTranscript(transcript);
            } else {
                console.warn("[SpeechInput] No transcript received");
                setErrorMessage(result.message || t("voiceInput.noSpeech", "No speech was detected."));
            }
        } catch (error) {
            clearTimeout(timeoutId); // Clear timeout on error too

            if ((error as DOMException).name === "AbortError") {
                // Check if this was caused by timeout or manual abort
                if (!transcriptionAbortRef.current || controller === transcriptionAbortRef.current) {
                    console.error("[SpeechInput] Transcription timed out after 30 seconds");
                    setErrorMessage(
                        t("voiceInput.timeout", "Transcription took too long. Please try again with a shorter recording.")
                    );
                } else {
                    console.log("[SpeechInput] Transcription aborted by user action");
                    // Silent return for user-initiated abort
                    return;
                }
            } else {
                console.error("[SpeechInput] Failed to transcribe audio:", error);
                // More specific error message for transcription phase
                setErrorMessage(
                    t("voiceInput.transcriptionError", "We couldn't transcribe your audio. Please try again.")
                );
            }
        } finally {
            setIsTranscribing(false);
            transcriptionAbortRef.current = null;
        }
    };

    const handleEnd = async () => {
        if (!isRecording && !isPaused) {
            return;
        }

        // Calculate recording duration
        const recordingDuration = (Date.now() - recordingStartTimeRef.current) / 1000; // in seconds
        console.log(`[SpeechInput] Ending recording, duration: ${recordingDuration.toFixed(1)}s`);

        setIsRecording(false);
        setIsPaused(false);

        const audioBlob = await collectRecording();
        console.log(`[SpeechInput] Collected audio blob: size=${audioBlob?.size || 0} bytes, type=${audioBlob?.type || 'none'}`);

        // Validate recording duration (minimum 0.5 seconds like meetings)
        if (recordingDuration < 0.5) {
            console.warn("[SpeechInput] Recording too short:", recordingDuration, "seconds");
            setErrorMessage(t("voiceInput.tooShort", "Recording too short. Please speak for at least 1 second."));
            return;
        }

        // Increase minimum size check from 1000 to 5000 bytes for better validation
        const MIN_BLOB_SIZE = 5000;
        if (!audioBlob || audioBlob.size < MIN_BLOB_SIZE) {
            console.warn(`[SpeechInput] Audio blob too small: ${audioBlob?.size || 0} bytes (minimum: ${MIN_BLOB_SIZE})`);
            setErrorMessage(t("voiceInput.noSpeech", "No speech was detected."));
            return;
        }

        console.log("[SpeechInput] Sending audio for transcription...");
        await transcribeAudio(audioBlob);
    };

    const statusHeadline = isTranscribing
        ? t("voiceInput.transcribingTitle", "Transcribing voice input")
        : isPaused
            ? t("voiceInput.paused", "Recording paused")
            : t("voiceInput.listening", "Listening…");

    const statusSubheading = isTranscribing
        ? t("voiceInput.transcribingSubtitle", "Hang tight while we convert your speech to text.")
        : isPaused
            ? t("voiceInput.resumeHint", "Resume when you're ready to continue.")
            : t("voiceInput.instructions", "Speak clearly and we'll transcribe your note.");

    const detectedLanguageLabel = useMemo(() => {
        if (!detectedLanguage) {
            return null;
        }
        const DisplayNamesCtor = (Intl as any).DisplayNames;
        if (DisplayNamesCtor) {
            try {
                const displayNames = new DisplayNamesCtor([i18n.language], { type: "language" });
                return displayNames.of(detectedLanguage) ?? detectedLanguage;
            } catch {
                return detectedLanguage;
            }
        }
        return detectedLanguage;
    }, [detectedLanguage, i18n.language]);

    const panelContent = (
        <div className={`${styles.voiceRecorderPanel} ${isPaused ? styles.voiceRecorderPaused : ""}`}>
            <div className={styles.voiceRecorderStatus}>
                <div className={styles.voiceRecorderIcon}>
                    <Mic28Filled primaryFill="rgba(97, 85, 245, 1)" />
                </div>
                <div>
                    <p className={styles.voiceRecorderHeadline}>{statusHeadline}</p>
                    <p className={styles.voiceRecorderSubheadline}>{statusSubheading}</p>
                </div>
            </div>
            {!isTranscribing && (
                <div className={styles.voiceRecorderWave} aria-hidden="true">
                    {waveformValues.map((value, index) => (
                        <span key={index} style={{ height: `${Math.round(10 + value * 46)}px` }} />
                    ))}
                </div>
            )}
            {isTranscribing && (
                <div className={styles.voiceRecorderTranscribing}>
                    <div className={styles.voiceRecorderSpinner} />
                    <span>{t("voiceInput.transcribing", "Transcribing your voice...")}</span>
                </div>
            )}
            {errorMessage && <div className={styles.voiceRecorderError}>{errorMessage}</div>}
            {!isTranscribing && (
                <div className={styles.voiceRecorderControls}>
                    {isPaused ? (
                        <Button appearance="secondary" icon={<Play size={16} />} onClick={resumeRecording}>
                            {t("voiceInput.resume", "Resume")}
                        </Button>
                    ) : (
                        <Button appearance="secondary" icon={<Pause size={16} />} onClick={pauseRecording}>
                            {t("voiceInput.pause", "Pause")}
                        </Button>
                    )}
                    <Button appearance="primary" icon={<Square size={16} />} onClick={handleEnd}>
                        {t("voiceInput.endRecording", "End & Insert")}
                    </Button>
                </div>
            )}
        </div>
    );

    const showPanel = isRecording || isPaused || isTranscribing;

    const buttonDisabled = !isSupported || isRecording || isTranscribing;

    const buttonTooltip = !isSupported
        ? t("voiceInput.unsupported", "Voice input isn't supported in this browser.")
        : isRecording
            ? t("voiceInput.recordingActive", "Recording in progress")
            : isTranscribing
                ? t("voiceInput.transcribingTooltip", "Transcribing your recording")
                : t("tooltips.askWithVoice");

    const voicePanel = showPanel
        ? visualizerContainer
            ? createPortal(panelContent, visualizerContainer)
            : panelContent
        : null;

    return (
        <>
            <Tooltip content={buttonTooltip} relationship="label">
                <span>
                    <Button
                        size="large"
                        appearance={isRecording ? "primary" : "secondary"}
                        icon={<Mic28Filled primaryFill={isRecording ? "rgba(97, 85, 245, 1)" : "rgba(115, 118, 225, 1)"} />}
                        disabled={buttonDisabled}
                        aria-label={buttonTooltip}
                        onClick={beginRecording}
                    />
                </span>
            </Tooltip>
            {!showPanel && errorMessage && <div className={styles.voiceRecorderInlineError}>{errorMessage}</div>}
            {!showPanel && detectedLanguageLabel && (
                <div className={styles.voiceRecorderInlineInfo}>
                    {t("voiceInput.detectedLanguage", "Detected language: {{language}}", { language: detectedLanguageLabel })}
                </div>
            )}
            {voicePanel}
        </>
    );
};
